// Printable "info card" PDF export — owner-only feature.
//
// Layout: US Letter portrait, two 5×7" landscape cards stacked vertically.
// Each card leaves ~0.5" of clear space at the top for a ringed binding.
// Cut/crop marks are printed at each card corner + a light dashed cut line
// between cards so a print shop (or the user with a paper cutter at home)
// can trim reliably. Cards are RGB (jsPDF's native) — most print shops
// accept RGB for short-run photo cards.
//
// Content per card:
//   • Photo (left side, ~3" × ~3.5" landscape)
//   • Common name (large bold serif)
//   • Scientific name (italic, smaller)
//   • Native pill (only if is_state_native === true)
//   • Keystone pill (only if is_keystone === true AND native)
//   • QR code linking to the public share URL for that plant
//
// The caller passes an array of `{ plant, shareUrl }` and this module
// generates the PDF and triggers download.

import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { localizedIdentification } from "@/lib/localizedId";

// --- Page geometry (all in PDF points; 1pt = 1/72 in) ---
const PAGE_W = 612;                                // 8.5"
const PAGE_H = 792;                                // 11"
const CARD_W = 7 * 72;                             // 504 pt (7")
const CARD_H = 5 * 72;                             // 360 pt (5")
const PAGE_MARGIN_X = (PAGE_W - CARD_W) / 2;       // 54 pt (0.75")
const CARDS_PER_PAGE = 2;
// Vertical layout: 18pt top+bottom margin, two 360pt cards, 36pt gap
// between them (0.5" — plenty of clearance for the cutting line and any
// crop marks that overhang the artwork).
const TOP_MARGIN = 18;
const CARD_INNER_GAP = PAGE_H - TOP_MARGIN * 2 - CARD_H * CARDS_PER_PAGE; // 36pt

// --- Card interior geometry ---
const BINDING_ZONE_H = 40;                         // clear space at top for hole punches
const CARD_PADDING = 18;                           // padding inside the card art below binding zone
const PHOTO_W = 220;
const PHOTO_H = CARD_H - BINDING_ZONE_H - CARD_PADDING * 2; // ~266pt

const decodeHtml = (s) => (typeof s === "string"
  ? s.replace(/&#x2F;/g, "/").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
  : s);

// Draw an L-shaped crop mark just outside a rectangle corner. Small ticks
// only — professional standard is ~9pt tick length, ~2pt away from the
// artwork edge.
const drawCropMarks = (doc, x, y, w, h) => {
  const t = 9;   // tick length
  const off = 2; // gap between artwork and tick
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  const line = (x1, y1, x2, y2) => doc.line(x1, y1, x2, y2);
  // Top-left
  line(x - off - t, y, x - off, y);
  line(x, y - off - t, x, y - off);
  // Top-right
  line(x + w + off, y, x + w + off + t, y);
  line(x + w, y - off - t, x + w, y - off);
  // Bottom-left
  line(x - off - t, y + h, x - off, y + h);
  line(x, y + h + off, x, y + h + off + t);
  // Bottom-right
  line(x + w + off, y + h, x + w + off + t, y + h);
  line(x + w, y + h + off, x + w, y + h + off + t);
};

// Faint dashed cut line horizontally across the page between two cards, so
// a home printer can eyeball the cut too.
const drawCutLine = (doc, y) => {
  doc.setDrawColor(160);
  doc.setLineWidth(0.4);
  doc.setLineDashPattern([3, 3], 0);
  doc.line(PAGE_MARGIN_X - 6, y, PAGE_MARGIN_X + CARD_W + 6, y);
  doc.setLineDashPattern([], 0);
};

const drawImageSafe = (doc, dataUrl, x, y, w, h) => {
  if (!dataUrl) {
    doc.setDrawColor(210);
    doc.setLineWidth(0.5);
    doc.rect(x, y, w, h);
    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text("No photo", x + w / 2, y + h / 2, { align: "center", baseline: "middle" });
    return;
  }
  try {
    const type = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
    // "SLOW" = jsPDF's best-quality compression profile. The extra CPU is
    // negligible next to the QR encoding, and the payoff is a card that
    // prints crisp instead of blurry at 300+ DPI.
    doc.addImage(dataUrl, type, x, y, w, h, undefined, "SLOW");
  } catch {
    doc.setDrawColor(210);
    doc.rect(x, y, w, h);
  }
};

// Draw a single pill (light border + label) with automatic width.
const drawPill = (doc, x, y, label, { bg = [230, 239, 233], fg = [45, 74, 34] } = {}) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const padX = 8;
  const w = doc.getTextWidth(label) + padX * 2;
  const h = 16;
  doc.setFillColor(bg[0], bg[1], bg[2]);
  doc.roundedRect(x, y, w, h, 8, 8, "F");
  doc.setTextColor(fg[0], fg[1], fg[2]);
  doc.text(label, x + padX, y + 11);
  return w;
};

// Draw a single card at (originX, originY). Returns nothing.
async function drawCard(doc, originX, originY, { plant, shareUrl, language, t }) {
  const id = localizedIdentification(plant, language) || {};
  const common = decodeHtml(id.common_name || t("printCards.unknown") || "Unknown");
  const sci = decodeHtml(id.scientific_name || "");
  const isNative = id.is_state_native === true;
  const isKeystone = isNative && id.is_keystone === true;
  const photo = plant.image_base64 || plant.thumb_base64;

  // Card outline (very thin gray, doubles as visual bleed reference)
  doc.setDrawColor(220);
  doc.setLineWidth(0.4);
  doc.rect(originX, originY, CARD_W, CARD_H);

  // Binding zone marker: thin dotted rule below the reserved space so
  // whoever hole-punches knows where the artwork starts.
  doc.setDrawColor(200);
  doc.setLineDashPattern([1.5, 2.5], 0);
  doc.line(originX + 24, originY + BINDING_ZONE_H, originX + CARD_W - 24, originY + BINDING_ZONE_H);
  doc.setLineDashPattern([], 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160);
  doc.text(t("printCards.bindingZone") || "Reserved for binding", originX + CARD_W / 2, originY + BINDING_ZONE_H - 6, { align: "center" });

  // Content origin
  const contentX = originX + CARD_PADDING;
  const contentY = originY + BINDING_ZONE_H + CARD_PADDING;
  const contentW = CARD_W - CARD_PADDING * 2;
  const contentH = CARD_H - BINDING_ZONE_H - CARD_PADDING * 2;

  // Photo (left column)
  drawImageSafe(doc, photo, contentX, contentY, PHOTO_W, Math.min(PHOTO_H, contentH));

  // Info column
  const infoX = contentX + PHOTO_W + 20;
  const infoW = contentW - PHOTO_W - 20;
  let infoY = contentY + 6;

  // Common name — auto-shrink from 26pt down to 16pt to fit two lines max.
  const fitCommonName = (text, maxW) => {
    let size = 26;
    while (size > 14) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(text, maxW);
      if (lines.length <= 2) return { size, lines };
      size -= 1;
    }
    doc.setFontSize(14);
    return { size: 14, lines: doc.splitTextToSize(text, maxW) };
  };
  const { size: nameSize, lines: nameLines } = fitCommonName(common, infoW);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(nameSize);
  doc.setTextColor(24);
  const lineHeight = nameSize + 3;
  nameLines.slice(0, 2).forEach((line, i) => {
    doc.text(line, infoX, infoY + (i + 1) * lineHeight);
  });
  infoY += nameLines.slice(0, 2).length * lineHeight + 6;

  // Scientific name (italic)
  if (sci) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(12);
    doc.setTextColor(90);
    const sciLines = doc.splitTextToSize(sci, infoW);
    sciLines.slice(0, 2).forEach((line, i) => {
      doc.text(line, infoX, infoY + (i + 1) * 14);
    });
    infoY += Math.min(2, sciLines.length) * 14 + 8;
  }

  // Native / Keystone pills
  if (isNative) {
    const w1 = drawPill(doc, infoX, infoY, t("printCards.pillNative") || "Native");
    if (isKeystone) {
      drawPill(doc, infoX + w1 + 6, infoY, t("printCards.pillKeystone") || "Keystone", { bg: [251, 235, 199], fg: [122, 78, 8] });
    }
    infoY += 22;
  }

  // QR code — bottom-right of info column, fixed 90×90pt
  if (shareUrl) {
    try {
      const qrDataUrl = await QRCode.toDataURL(shareUrl, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 300,
        color: { dark: "#000000", light: "#FFFFFF" },
      });
      const qrSize = 90;
      const qrX = originX + CARD_W - CARD_PADDING - qrSize;
      const qrY = originY + CARD_H - CARD_PADDING - qrSize - 14;
      doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(120);
      doc.text(t("printCards.qrCaption") || "Scan for full plant info", qrX + qrSize / 2, qrY + qrSize + 9, { align: "center" });
    } catch {
      // QR failure is non-fatal — just skip the code and continue.
    }
  }
}

/**
 * Generate a printable info-cards PDF and trigger a download.
 *
 * @param {Array<{ plant: object, shareUrl: string }>} cards
 * @param {{ language: string, t: (key: string) => string, filename?: string }} opts
 */
export async function exportPrintCardsPdf(cards, { language, t, filename = "prairie_info_cards.pdf" } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });

  for (let i = 0; i < cards.length; i++) {
    const pageIdx = Math.floor(i / CARDS_PER_PAGE);
    const slotOnPage = i % CARDS_PER_PAGE;
    if (i > 0 && slotOnPage === 0) doc.addPage();

    const originX = PAGE_MARGIN_X;
    const originY = TOP_MARGIN + slotOnPage * (CARD_H + CARD_INNER_GAP);

    // Draw art
    await drawCard(doc, originX, originY, { ...cards[i], language, t });

    // Crop marks around this card
    drawCropMarks(doc, originX, originY, CARD_W, CARD_H);

    // If this is the top card on a page AND there's a second card, add a
    // cut line halfway through the gap between the two cards.
    if (slotOnPage === 0 && i + 1 < cards.length) {
      drawCutLine(doc, originY + CARD_H + CARD_INNER_GAP / 2);
    }
  }

  // Tiny footer with page x/y — helps at the print shop when reassembling
  // a large batch.
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(160);
    doc.text(
      `Prairie Info Cards · Page ${p} of ${totalPages} · 5×7 in. landscape`,
      PAGE_W / 2,
      PAGE_H - 8,
      { align: "center" }
    );
  }

  doc.save(filename);
}
